import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { openExternal, useAuthStore, useCreateAccount } from '@wordpuppi/shared';
import { toast } from 'sonner';
import {
  Bell,
  BookOpen,
  Check,
  CircleUserRound,
  CreditCard,
  Info,
  LogOut,
  Plus,
  Search,
  Settings,
  SunMoon,
  Users,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useActiveAccount } from '@/lib/activeAccount';

/** "Wordpuppi" → "WO"; "Acme Co" → "AC". */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.trim().slice(0, 2).toUpperCase();
}

const rowClass =
  'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none select-none cursor-default hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground';

function Avatar({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-md bg-accent text-[10px] font-semibold text-foreground',
        className,
      )}
    >
      {initials(name || '?')}
    </span>
  );
}

/**
 * Stripe-style account menu (AB#611). Identity header → account settings →
 * a searchable, checkmarked account switcher → create account → user + sign out.
 *
 * Built on Popover, not the DropdownMenu: base-ui's Menu drives typeahead through
 * floating-ui's useTypeahead, which preventDefaults every character key while
 * open — an embedded search <input> would never receive typed text there. The
 * searchable-popup pattern in this app (platforms.tsx) uses Popover for exactly
 * this reason. Theme selection lives in the header ThemeToggle, not this menu.
 *
 * `trigger` swaps the default header icon for a custom element (e.g. the site
 * sidebar's Account button, AB#611) — the popover contents and behavior are
 * unchanged; `side`/`align` just place it next to whatever opened it.
 */
export function AccountMenu({
  trigger,
  side = 'bottom',
  align = 'end',
}: {
  trigger?: React.ReactElement;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
} = {}) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { accounts, active: activeAccount, setActive } = useActiveAccount();
  const createAccount = useCreateAccount();

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const filteredAccounts = q
    ? accounts.filter((a) => a.name.toLowerCase().includes(q))
    : accounts;

  const go = (to: string) => {
    setOpen(false);
    navigate({ to });
  };

  const switchTo = (pid: string) => {
    setActive(pid);
    setOpen(false);
    navigate({ to: '/sites' });
  };

  // Create-account dialog (backend supports it via useCreateAccount → POST /accounts).
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      const res = await createAccount.mutateAsync(name);
      if (res.data?.pid) setActive(res.data.pid);
      toast.success(`Account "${name}" created`);
      setNewName('');
      setCreateOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create account');
    }
  };

  const headerName = activeAccount?.name ?? user?.name ?? 'Your account';
  const headerSubtitle = activeAccount?.role
    ? activeAccount.role.charAt(0).toUpperCase() + activeAccount.role.slice(1)
    : user?.email;

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          aria-label="Account menu"
          className={
            trigger
              ? undefined
              : 'inline-flex size-9 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50'
          }
          render={trigger}
        >
          {trigger ? undefined : <CircleUserRound className="size-5" />}
        </PopoverTrigger>
        <PopoverContent side={side} align={align} className="w-72 p-1">
          {/* Identity header */}
          <div className="flex items-center gap-2.5 px-2 py-2">
            <Avatar name={headerName} className="size-9 text-xs" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{headerName}</p>
              {headerSubtitle && (
                <p className="truncate text-xs text-muted-foreground">{headerSubtitle}</p>
              )}
            </div>
          </div>

          <div className="my-1 h-px bg-border" />

          {/* Account switcher (AB#1014) — first thing under the header, so "which
              account am I in / take me to the other one" is answered where the
              menu opens, not below the help links. A lone account is already
              the header, so the list only appears once there is somewhere to go. */}
          {accounts.length > 1 && (
            <>
              <p className="px-2 pt-1 pb-0.5 text-xs font-medium text-muted-foreground">
                Switch account
              </p>
              {accounts.length > 5 && (
                <div className="relative px-1 py-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    aria-label="Search accounts"
                    placeholder="Search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="h-8 pl-8"
                  />
                </div>
              )}
              <div className="max-h-56 overflow-y-auto">
                {filteredAccounts.map((a) => {
                  const isActive = a.pid === activeAccount?.pid;
                  return (
                    <button
                      key={a.pid}
                      type="button"
                      className={rowClass}
                      onClick={() => switchTo(a.pid)}
                    >
                      <Avatar name={a.name} className="size-6" />
                      <span
                        className={cn(
                          'flex-1 truncate text-left',
                          isActive && 'font-semibold',
                        )}
                      >
                        {a.name}
                      </span>
                      {a.member_count > 1 && (
                        <Badge variant="secondary" className="px-1.5 text-[10px]">
                          {a.member_count}
                        </Badge>
                      )}
                      {isActive && <Check className="size-4 shrink-0 text-primary" />}
                    </button>
                  );
                })}
                {filteredAccounts.length === 0 && (
                  <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                    No accounts found
                  </p>
                )}
              </div>
            </>
          )}
          <button
            type="button"
            className={rowClass}
            onClick={() => {
              setOpen(false);
              setCreateOpen(true);
            }}
          >
            <Plus className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">Create account</span>
          </button>

          <div className="my-1 h-px bg-border" />

          {/* Account settings entries */}
          <button type="button" className={rowClass} onClick={() => go('/account')}>
            <Settings className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">Account settings</span>
          </button>
          <button type="button" className={rowClass} onClick={() => go('/account/team')}>
            <Users className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">Team</span>
          </button>
          <button type="button" className={rowClass} onClick={() => go('/account/billing')}>
            <CreditCard className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">Billing</span>
          </button>
          <button
            type="button"
            className={rowClass}
            onClick={() => go('/account/notifications')}
          >
            <Bell className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">Notifications</span>
          </button>

          <button type="button" className={rowClass} onClick={() => go('/about')}>
            <Info className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">About WordPuppi</span>
          </button>
          <button type="button" className={rowClass} onClick={() => {
            setOpen(false);
            void openExternal('https://docs.wordpuppi.com/docs/getting-started');
          }}>
            <BookOpen className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">Documentation</span>
          </button>

          <div className="my-1 h-px bg-border" />

          {/* Logged-in user + sign out */}
          {user && (
            <div className="flex items-center gap-2.5 px-2 py-1.5">
              <CircleUserRound className="size-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">{user.name || 'You'}</p>
                {user.email && (
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                )}
              </div>
            </div>
          )}
          {/* Personal settings sit with the person, under the identity block —
              the rows above are account-level. */}
          <button type="button" className={rowClass} onClick={() => go('/user')}>
            <CircleUserRound className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">User settings</span>
          </button>
          <button type="button" className={rowClass} onClick={() => go('/user/appearance')}>
            <SunMoon className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">Appearance</span>
          </button>
          <button
            type="button"
            className={cn(rowClass, 'text-destructive hover:bg-destructive/10 hover:text-destructive')}
            onClick={() => {
              setOpen(false);
              setActive(null);
              logout();
            }}
          >
            <LogOut className="size-4 shrink-0 text-muted-foreground" />
            <span className="flex-1 text-left">Log out</span>
          </button>
        </PopoverContent>
      </Popover>

      {/* Create account dialog (rendered outside the popover so it survives its close). */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Create account</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-account-name">Account name</Label>
              <Input
                id="new-account-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Acme Marketing"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!newName.trim() || createAccount.isPending}>
                {createAccount.isPending ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
